// Formatação de prazos e durações no idioma do app ("Hoje, 14:00", "1 h 15 min").
import type { TFunction } from 'i18next';
import { diffDays, formatTime, parseDateKey, toDateKey } from '../domain/dates';

export function formatMinutes(minutes: number, t: TFunction): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return t('time.minutes', { count: m });
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? t('time.hoursMinutes', { h, m: rest }) : t('time.hours', { h });
}

/** Duração em segundos: "45 s", "1 min 30 s", "1 h", "2 h 15 min" (descanso do Pomodoro). */
export function formatDuration(seconds: number, t: TFunction): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h) parts.push(t('time.h', { n: h }));
  if (m) parts.push(t('time.m', { n: m }));
  if (s || !parts.length) parts.push(t('time.s', { n: s }));
  return parts.join(' ');
}

/** Tempo gasto, arredondado para baixo ao minuto ("0 min", "1 h 30 min"). */
export function formatSpent(seconds: number, t: TFunction): string {
  return formatMinutes(Math.floor(seconds / 60), t);
}

function weekdayShort(key: string, lang: string): string {
  const s = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(parseDateKey(key)).replace('.', '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function dayMonth(key: string, lang: string): string {
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short' }).format(parseDateKey(key)).replace('.', '');
}

export function formatDue(dueDate: string, dueTime: string | null, now: Date, lang: string, t: TFunction, timeFormat: '12h' | '24h' = '24h'): string {
  const days = diffDays(toDateKey(now), dueDate);
  let day: string;
  if (days === 0) day = t('due.today');
  else if (days === -1) day = t('due.yesterday');
  else if (days === 1) day = t('due.tomorrow');
  else if (days > 1 && days < 7) day = weekdayShort(dueDate, lang);
  else day = dayMonth(dueDate, lang);
  return dueTime ? `${day}, ${formatTime(dueTime, timeFormat)}` : day;
}

export function formatLongDate(now: Date, lang: string): string {
  const s = new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' }).format(now);
  return s.charAt(0).toUpperCase() + s.slice(1);
}
